import type { PropsWithChildren } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Card } from 'aem-libs-react-components/src/components/common/card'
import { Flex } from 'aem-libs-react-components/src/components/common/flex'
import { Button } from 'aem-libs-react-components/src/components/forms/button'
import { ButtonLink } from 'aem-libs-react-components/src/components/forms/button/button-link'
import type { ButtonThemeType } from 'aem-libs-react-components/src/components/forms/button/types'
import { QueryProvider } from 'aem-libs-react-components/src/components/utility/query-provider/QueryProvider'
import { useDeeplink } from 'aem-libs-react-ts/src/hooks/useDeeplink'
import { useDynamicParams } from 'aem-libs-react-ts/src/hooks/useDynamicParams'
import { useScrollOnMount } from 'aem-libs-react-ts/src/hooks/useScrollOnMount'
import LanguageDetector from 'aem-libs-react-ts/src/services/LanguageDetector'
import { gaTracker } from 'aem-libs-react-ts/src/utils/gaTracker'
import { isMobile } from 'aem-libs-react-ts/src/utils/isMobile'

import callMeIco from '../assets/callme-cta.svg'
import georgeIco from '../assets/george-cta.svg'
import noClientIco from '../assets/no-client-cta.svg'
import { testIds } from '../e2e/testIds'
import type { MembershipParams } from '../types'
import { CardContent, Cards } from './App.styled'


const CardLayout = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr 48px;
`

const CalculatorContent = styled.div`
  text-align: center;

  h1 {
    font-weight: 700;
    font-size: 34px !important;
    color: ${({ theme }) => theme.colors.font.white} !important;
  }

  p {
    font-size: 20px !important;
  }

  margin-bottom: 45px;
`

interface CtaCardProps {
  text: string
  icon: string
}

const CtaCard = ({ text, icon, children }: PropsWithChildren<CtaCardProps>) => {
  return (
    <Card textAlign={'center'}>
      <CardLayout>
        <CtaIco src={icon} alt='' />
        <CardContent dangerouslySetInnerHTML={{ __html: text }} />
        <Flex justifyContent={'center'}>{children}</Flex>
      </CardLayout>
    </Card>
  )
}

const CtaIco = styled.img`
  margin: auto;
`

const lang = LanguageDetector()

export const MembershipAllowed = () => {
  const {
    georgeContent,
    noClientContent,
    callMeContent,
    callMeButton,
    noClientButton,
    georgeButtonMobile,
    georgeButtonDesktop,
    allowedContent
  } = useDynamicParams<MembershipParams>()

  const containerRef = useScrollOnMount<HTMLDivElement>()

  const deeplink = useDeeplink({
    url: georgeButtonMobile.url[lang],
    fallbackPath: georgeButtonMobile.fallbackPath,
    hash: georgeButtonMobile.hash,
    medium: georgeButtonMobile.medium
  })

  const georgeMeta = useMemo<{ label: string; link: string; deeplink: boolean; theme: ButtonThemeType }>(() => {
    if (isMobile()) {
      return {
        label: georgeButtonMobile.label[lang],
        link: deeplink.link,
        deeplink: true,
        theme: georgeButtonMobile.theme ?? 'blue'
      }
    }

    return {
      deeplink: false,
      label: georgeButtonDesktop.label[lang],
      link: georgeButtonDesktop.url[lang],
      theme: georgeButtonDesktop.theme ?? 'blue'
    }
  }, [georgeButtonMobile, georgeButtonDesktop])

  return (
    <QueryProvider>
      <CalculatorContent ref={containerRef}>
        <CardContent data-testid={testIds.allowedContent} dangerouslySetInnerHTML={{ __html: allowedContent }} />
      </CalculatorContent>
      <Cards>
        <CtaCard text={georgeContent} icon={georgeIco}>
          <Button
            id={'george-cta'}
            data-testid={testIds.georgeButton}
            type={'button'}
            buttonTheme={georgeMeta.theme}
            onClick={() => {
              gaTracker.onRedirect({
                deepLink: georgeMeta.deeplink,
                elementText: georgeMeta.label,
                elementID: 'george-cta',
                url: georgeMeta.link
              })
              if (isMobile()) {
                deeplink.redirect()
                return
              }

              document.location = georgeMeta.link
            }}
          >
            {georgeMeta.label}
          </Button>
        </CtaCard>
        <CtaCard text={noClientContent} icon={noClientIco}>
          <ButtonLink
            data-testid={testIds.noClientButton}
            href={noClientButton.url[lang]}
            type={'button'}
            buttonTheme={noClientButton.theme}
          >
            {noClientButton.label[lang]}
          </ButtonLink>
        </CtaCard>
        <CtaCard text={callMeContent} icon={callMeIco}>
          <ButtonLink
            data-testid={testIds.callMeButton}
            href={callMeButton.url[lang]}
            type={'button'}
            buttonTheme={callMeButton.theme}
          >
            {callMeButton.label[lang]}
          </ButtonLink>
        </CtaCard>
      </Cards>
    </QueryProvider>
  )
}
